import { __awaiter } from "tslib";
/**
 * YearlyCalendarPage - Standalone page wrapper for YearlyCalendar
 * Loads real calendar data from existing GRM/Availability APIs
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { YearlyCalendar } from './components/YearlyCalendar';
import { DEFAULT_CALENDAR_CONFIG } from '../../../Configuration/YearlyCalendarConfiguration/CalendarConfig';
import { DEFAULT_POPUP_SETTINGS } from '../../../Configuration/YearlyCalendarConfiguration/PopupSettingsConfig';
import YearlyCalendarBridgeService from '../../services/YearlyCalendarBridge.service';
import { EventDetailModal } from './components/EventDetailModal/EventDetailModal';
import { AddEditEventModal } from './components/AddEditEventModal/AddEditEventModal';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal/DeleteConfirmationModal';
import { CalendarViewSwitcher } from './components/CalendarViewSwitcher/CalendarViewSwitcher';
import { ResourceNamePicker } from './components/ResourceNamePicker/ResourceNamePicker';
import { MiniMonth } from './components/MiniMonth';
import { useCalendarViewGate, DEFAULT_CALENDAR_MODE_PREFERENCE, MONTHLY, YEARLY, } from './hooks/useCalendarViewGate';
import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { TelemetryService } from '../../services/TelemetryService';
import { CalendarAuthorizationService } from '../../services/CalendarAuthorization.service';
import { EventCodes, TelemetryActions, TelemetryFeatures } from '../../../common/TelemetryConstants';
/**
 * sessionStorage key recording which surface navigated the user into this page.
 *
 * Cross-repo contract: `ce-calendar-header` (ra-ux-web) writes 'cescalendar' immediately before
 * handing off to /yearlycalendar, because /cescalendar has no yearly view of its own
 * (`enabledViewTypes: [WEEK, WORKING_WEEK, MONTH]`, ce-calendar.component.ts:199). Switching back
 * to Monthly must therefore return the user to the CES month grid rather than dropping them into
 * the Resource Dashboard's monthly view.
 *
 * Any other entry (resource dashboard, left-nav, direct URL) leaves no marker and switches to the
 * RD monthly view client-side.
 */
const MONTHLY_SOURCE_KEY = 'ra-yearly-monthly-source';
const pageStyles = {
    container: {
        padding: '0',
        backgroundColor: '#f3f2f1',
        minHeight: '100vh',
        boxSizing: 'border-box',
    },
    userBanner: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 600,
        color: '#0063B1',
        backgroundColor: '#f5f5f6',
        borderBottom: 'none',
    },
    header: {
        margin: '0',
        padding: '12px 20px',
        backgroundColor: '#ffffff',
        borderBottom: 'none',
    },
    title: {
        margin: 0,
        fontSize: '20px',
        fontWeight: 600,
        color: '#323130',
    },
    backLink: {
        color: '#0078d4',
        fontSize: '14px',
        fontWeight: 400,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
        marginLeft: 'auto',
    },
    subtitle: {
        margin: '8px 0 0 0',
        fontSize: '14px',
        color: '#605e5c',
    },
    calendarWrapper: {
        backgroundColor: '#ffffff',
        padding: '0',
        border: 'none',
        marginTop: '25px',
        marginLeft: '15px',
        marginRight: '15px',
        marginBottom: '15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 -2px 8px rgba(0,0,0,0.06), 2px 0 8px rgba(0,0,0,0.06), -2px 0 8px rgba(0,0,0,0.06)',
    },
    toast: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    toastSuccess: {
        backgroundColor: '#dff6dd',
        color: '#107c10',
        borderBottom: '1px solid #a7e3a5',
    },
    toastError: {
        backgroundColor: '#fde7e9',
        color: '#a80000',
        borderBottom: '1px solid #f1bbbc',
    },
    toastDismiss: {
        background: 'none',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer',
        padding: '2px 6px',
        lineHeight: 1,
        color: 'inherit',
    },
};
const getStartOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
const getStartOfYear = (date) => new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);
const getEndOfYear = (date) => new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);
/**
 * How many months the monthly view renders: the main month plus the two side
 * mini-months. Legacy requests exactly this window
 * (calendar.controller.js:355-357 -> StartDate = 1st of month,
 * EndDate = last day of month + 2).
 */
export const MONTHLY_VIEW_MONTH_SPAN = 3;
/**
 * Resolve the API date window for a view mode.
 *
 * Exported for contract testing: monthly MUST stay a strict subset of the
 * yearly window for the same active month, otherwise the client-side
 * Monthly/Yearly switch would silently serve a partially-populated grid.
 */
export const getCalendarRange = (activeDate, mode) => {
    if (mode === 'year' || mode === 'richcc-year') {
        // The yearly calendar grid shows a rolling 12 months starting from
        // activeDate's month.  Fetch the full range so that months wrapping
        // into the next calendar year (e.g. Jan-Mar 2026 when viewing
        // Apr 2025 – Mar 2026) are included in the API response.
        const startDate = new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
        const endDate = new Date(activeDate.getFullYear(), activeDate.getMonth() + 12, 0, 23, 59, 59, 999);
        return { startDate, endDate };
    }
    // Monthly covers the main month AND both side mini-months; a single-month
    // window would leave the two side calendars silently empty.
    return {
        startDate: getStartOfMonth(activeDate),
        endDate: new Date(activeDate.getFullYear(), activeDate.getMonth() + MONTHLY_VIEW_MONTH_SPAN, 0, 23, 59, 59, 999),
    };
};
const getEventColors = (eventCategory, assignmentType, eventColorOverride) => {
    const category = (eventCategory || '').toLowerCase();
    const type = (assignmentType || '').toLowerCase();
    if (eventColorOverride) {
        return { bgcolor: eventColorOverride, color: '#ffffff' };
    }
    if (category === 'project') {
        return { bgcolor: '#008272', color: '#ffffff' };
    }
    if (category === 'nonproject') {
        // Public holidays arriving as nonproject events get holiday styling
        if (type.includes('holiday') || type.includes('public holiday')) {
            return { bgcolor: '#5C2D91', color: '#ffffff' };
        }
        if (type.includes('ross')) {
            return { bgcolor: '#A42E00', color: '#ffffff' };
        }
        return { bgcolor: '#0b400b', color: '#ffffff' };
    }
    if (category === 'msvacation' || category === 'publicholiday') {
        return { bgcolor: '#5C2D91', color: '#ffffff' };
    }
    return { bgcolor: '#a9a9a9', color: '#ffffff' };
};
const getOutlookEventColors = (status) => {
    const statusValue = (status || '').toLowerCase();
    if (statusValue.includes('oof') || statusValue.includes('busy')) {
        return { bgcolor: '#4098FC', color: '#ffffff' };
    }
    return { bgcolor: '#a9a9a9', color: '#ffffff' };
};
const mapCEEventToCalendarEvent = (event) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const category = (event.eventCategory || '').toLowerCase();
    const assignmentType = (_a = event.customProperties) === null || _a === void 0 ? void 0 : _a.AssignmentType;
    const eventColorOverride = (((_b = event.customProperties) === null || _b === void 0 ? void 0 : _b.EventColor) || ((_d = (_c = event.customProperties) === null || _c === void 0 ? void 0 : _c.AdditionalProperties) === null || _d === void 0 ? void 0 : _d.EventColor));
    const colors = getEventColors(category, assignmentType, eventColorOverride);
    const eventId = ((_e = event.customProperties) === null || _e === void 0 ? void 0 : _e.ResourceRequestId) || ((_f = event.customProperties) === null || _f === void 0 ? void 0 : _f.Id);
    const assignmentTypeLower = (assignmentType || '').toLowerCase();
    const isPublicHoliday = category === 'msvacation' || category === 'publicholiday'
        || (category === 'nonproject' && (assignmentTypeLower.includes('holiday') || assignmentTypeLower.includes('public holiday')));
    return {
        id: eventId !== null && eventId !== void 0 ? eventId : `${category}-${event.startDateTime}`,
        eventId: eventId,
        name: event.eventName,
        subject: event.eventName,
        startDt: new Date(event.startDateTime.replace('Z', '')),
        endDt: new Date(event.endDateTime.replace('Z', '')),
        startDtFQ: event.startDateTime,
        endDtFQ: event.endDateTime,
        bgcolor: colors.bgcolor,
        color: colors.color,
        isClickable: true,
        eventTypeName: event.eventCategory,
        isPublicHoliday,
        holidayType: isPublicHoliday ? 'Public Holiday' : undefined,
        ariaLabel: ((_g = event.customProperties) === null || _g === void 0 ? void 0 : _g.ShowAs) || ((_h = event.customProperties) === null || _h === void 0 ? void 0 : _h.Status),
        customProperties: Object.assign(Object.assign({}, event.customProperties), { 
            // Derived fields matching ce-calendar.component.ts logic:
            // isNonContiguousAssignment = Schedules array has more than 1 entry
            isNonContiguousAssignment: Array.isArray((_j = event.customProperties) === null || _j === void 0 ? void 0 : _j.Schedules) &&
                event.customProperties.Schedules.length > 1, 
            // Total scheduled hours = sum of Schedules[].durationInHours
            totalScheduledHours: Array.isArray((_k = event.customProperties) === null || _k === void 0 ? void 0 : _k.Schedules)
                ? event.customProperties.Schedules.reduce((sum, s) => sum + (s.durationInHours || 0), 0)
                : 0, 
            // Assignment schedules array for display
            assignmentSchedules: Array.isArray((_l = event.customProperties) === null || _l === void 0 ? void 0 : _l.Schedules)
                ? event.customProperties.Schedules
                : [] }),
        rawEvent: event,
    };
};
const mapOutlookEventToCalendarEvent = (event) => {
    const colors = getOutlookEventColors(event.status);
    return {
        id: `outlook-${event.startDateTime}-${event.subject}`,
        name: event.subject,
        subject: event.subject,
        startDt: new Date(event.startDateTime),
        endDt: new Date(event.endDateTime),
        startDtFQ: event.startDateTime,
        endDtFQ: event.endDateTime,
        bgcolor: colors.bgcolor,
        color: colors.color,
        isClickable: true,
        eventTypeName: 'outlook',
    };
};
const ALL_WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const getNonWorkDays = (workingDays) => {
    if (!workingDays || workingDays.length === 0) {
        // Default to Saturday/Sunday when no user work-week data is available
        return ['Saturday', 'Sunday'];
    }
    const normalizedWorkingDays = workingDays.map(day => day.toLowerCase());
    return ALL_WEEK_DAYS.filter(day => !normalizedWorkingDays.includes(day.toLowerCase()));
};
const startOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};
const isEditableFutureNonProjectEvent = (event) => {
    if (!event) {
        return false;
    }
    const category = (event.eventTypeName || '').toLowerCase();
    if (event.isPublicHoliday || category !== 'nonproject') {
        return false;
    }
    const eventStart = event.startDt instanceof Date ? event.startDt : new Date(event.startDt);
    if (isNaN(eventStart.getTime())) {
        return false;
    }
    return eventStart >= startOfToday();
};
/** Safe accessor for the FXP user info service; returns null when unavailable. */
const getUserInfoService = () => {
    try {
        return FxpServiceFactory.getServiceInstance(FXPServiceCatalog.UserInfoService);
    }
    catch (_a) {
        return null;
    }
};
/**
 * Route shapes this page owns.
 *
 * Both roots resolve to this component: `yearlycalendar` (React's own URL) and
 * `resourcedashboard` (the legacy GRM Resource Dashboard URL, claimed in
 * application.ts so existing GRM entry points need no JavaScript change).
 */
const CALENDAR_ROUTE_ROOTS = ['yearlycalendar', 'resourcedashboard'];
/**
 * Resolve the calendar route.
 *
 * Recognised shapes, for both roots above:
 *   /<root>                              -> self view
 *   /<root>/:alias/:role                 -> cross-user  (quick search)
 *   /<root>/:alias/:role/:managerAlias   -> cross-user  (functional manager)
 *
 * FAILS CLOSED. A URL that carries cross-user segments under a root this page does not own
 * returns `unrecognised` rather than quietly resolving to the viewer. The old behaviour --
 * defaulting any unmatched route to `{ alias: userAlias }` -- meant a manager who followed a
 * deep link to a resource's calendar would be shown their OWN calendar, and could edit it,
 * with no 404 and no error to signal the substitution.
 *
 * A short URL under an unknown root (no alias segment) is still treated as the self view: it
 * carries no cross-user intent to betray, and this component can legitimately be mounted at a
 * route that is not in the list above.
 */
export const resolveCalendarRoute = (hashOverride) => {
    let segments = [];
    try {
        const hash = (hashOverride !== null && hashOverride !== void 0 ? hashOverride : window.location.hash).replace(/^#\/?/, '');
        // Drop any query string / fragment suffix before splitting into path segments.
        segments = hash.split(/[?;]/)[0].split('/').filter(Boolean);
    }
    catch (_a) {
        return { kind: 'self' };
    }
    const isOwnedRoot = segments.length > 0
        && CALENDAR_ROUTE_ROOTS.indexOf(segments[0].toLowerCase()) !== -1;
    if (!isOwnedRoot) {
        return segments.length >= 2 ? { kind: 'unrecognised' } : { kind: 'self' };
    }
    if (segments.length < 2 || !segments[1]) {
        return { kind: 'self' };
    }
    return {
        kind: 'cross-user',
        params: {
            alias: segments[1],
            role: segments[2] || '',
            managerAlias: segments[3] || '',
        },
    };
};
const YearlyCalendarPage = () => {
    const userAlias = useMemo(() => { var _a, _b; return ((_b = (_a = getUserInfoService()) === null || _a === void 0 ? void 0 : _a.getCurrentUser) === null || _b === void 0 ? void 0 : _b.call(_a)) || ''; }, []);
    // Re-resolve the route whenever the URL hash changes within the same component mount. An
    // in-place hash navigation (e.g. switching between two users' calendars without a remount)
    // would otherwise leave `targetAlias`/`targetRole` - and therefore the authorization gate -
    // pointing at the previously viewed resource.
    const [hashRevision, setHashRevision] = useState(0);
    useEffect(() => {
        const onHashChange = () => setHashRevision((rev) => rev + 1);
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);
    // `unrecognised` means the URL carried cross-user intent under a root this page does not own --
    // it must NOT collapse to the viewer's own calendar (see resolveCalendarRoute), so it is
    // surfaced as an authorization failure below.
    const routeResolution = useMemo(() => resolveCalendarRoute(), [hashRevision]);
    const isUnrecognisedRoute = routeResolution.kind === 'unrecognised';
    const routeParams = useMemo(() => (routeResolution.kind === 'cross-user'
        ? routeResolution.params
        : { alias: userAlias, role: '', managerAlias: '' }), [routeResolution, userAlias]);
    const targetAlias = routeParams.alias;
    const targetRole = routeParams.role;
    const targetManagerAlias = routeParams.managerAlias;
    /**
     * Switch to another of this manager's reportees, staying on the FM shape of the route so the
     * picker survives the hop (parity: `LoadResourceCalendar`, calendar.controller.js:2968-2976,
     * which re-enters `grm-resourcedashboardfm` with the same `managerAlias`).
     *
     * Navigates by STATE NAME first, per the convention in `fmNavigation.ts`, and falls back to
     * assigning the hash when the FXP shell is absent (tests / local preview) or the state is not
     * registered. Either path lands on `yearlycalendar-fm` (`application.ts:135-136`), whose
     * `hashchange` re-resolves the route above and refetches the newly selected resource.
     *
     * We always target React's own `yearlycalendar` root rather than whichever root the user arrived
     * on. Both roots render this page, but `resourcedashboard` is the URL the GRM redirect watches
     * (§29b) — re-entering it would bounce the user back through that redirect on every selection.
     */
    const handleSelectResource = useCallback((alias) => {
        if (!alias || !targetRole || !targetManagerAlias) {
            return;
        }
        const fallbackHash = `#/yearlycalendar/${encodeURIComponent(alias)}/${encodeURIComponent(targetRole)}/${encodeURIComponent(targetManagerAlias)}`;
        try {
            const routeService = FxpServiceFactory.getServiceInstance(FXPServiceCatalog.FxpRouteService);
            if (routeService === null || routeService === void 0 ? void 0 : routeService.navigatetoSpecificState) {
                routeService.navigatetoSpecificState('yearlycalendar-fm', {
                    alias,
                    role: targetRole,
                    managerAlias: targetManagerAlias,
                });
                return;
            }
        }
        catch (_a) {
            // No FXP shell — fall through to the hash below.
        }
        window.location.hash = fallbackHash;
    }, [targetRole, targetManagerAlias]);
    const isCrossUserRoute = routeResolution.kind === 'cross-user';
    /*
     * Cross-user intent comes from the ROUTE, not from comparing aliases.
     *
     * Deriving it purely from `targetAlias !== userAlias` fails OPEN when the viewer's identity is
     * unavailable (`getCurrentUser()` returning ''): the comparison collapses to false, the page
     * calls itself a self view, and it would then read AND write another user's calendar with no
     * authorization check at all. If we cannot prove the target is the viewer, we must assume it
     * is not.
     */
    const isViewingOtherUser = useMemo(() => isCrossUserRoute
        && (!userAlias || !targetAlias || targetAlias.toLowerCase() !== userAlias.toLowerCase()), [isCrossUserRoute, targetAlias, userAlias]);
    /**
     * Derive initial non-work days before the API response arrives.
     *
     * Only valid for the viewer's OWN calendar: seeding a cross-user view from
     * `getCurrentUserData().Weekdays` would shade the resource's calendar with the
     * manager's work week. For another user we hold the neutral default until
     * their `workPreferences.workingDays` arrives with the calendar payload.
     */
    const initialNonWorkDays = useMemo(() => {
        var _a, _b, _c;
        if (isViewingOtherUser) {
            return getNonWorkDays();
        }
        const weekdays = (_c = (_b = (_a = getUserInfoService()) === null || _a === void 0 ? void 0 : _a.getCurrentUserData) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.Weekdays;
        if (weekdays && weekdays.length > 0) {
            return getNonWorkDays(weekdays);
        }
        return getNonWorkDays();
    }, [isViewingOtherUser]);
    const [targetTimeZone, setTargetTimeZone] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    // Resolve the entry source ONCE at mount, clearing the marker immediately so it cannot go stale
    // across later navigations. Consuming it here means a later visit that arrives WITHOUT a fresh
    // handoff correctly falls back to the Resource Dashboard monthly view, with no dependency on the
    // CES side clearing it.
    const [monthlySource] = useState(() => {
        try {
            const source = sessionStorage.getItem(MONTHLY_SOURCE_KEY);
            sessionStorage.removeItem(MONTHLY_SOURCE_KEY);
            return source;
        }
        catch (_a) {
            return null;
        }
    });
    const [activeDate, setActiveDate] = useState(new Date());
    const [currentMode, setCurrentMode] = useState('richcc-year');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [nonWorkDays, setNonWorkDays] = useState(initialNonWorkDays);
    const [npEventTypeNames, setNpEventTypeNames] = useState([]);
    // --- Toast message state ---
    const [toast, setToast] = useState(null);
    const upsertInFlightRef = useRef(false);
    // Monotonically increasing id used to ignore stale calendar responses: only the
    // most recent refreshEvents call is allowed to apply its results.
    const refreshRequestIdRef = useRef(0);
    // Date window (and alias) the events currently in state were fetched for.
    // Lets the Monthly/Yearly switch re-render from cache when the new view is
    // fully contained in what was already fetched.
    const fetchedWindowRef = useRef(null);
    const showToast = useCallback((message, type) => {
        setToast({ message, type });
        if (type === 'success') {
            setTimeout(() => setToast(null), 4000);
        }
    }, []);
    // --- Modal state ---
    const [detailModalEvent, setDetailModalEvent] = useState(null);
    const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null);
    const [addEditModal, setAddEditModal] = useState({ open: false, mode: 'add', targetDate: null, event: null });
    const yearlyCalendarBridge = useMemo(() => YearlyCalendarBridgeService.getSingleton(), []);
    const telemetryService = useMemo(() => TelemetryService.getInstance(), []);
    const telemetrySource = 'YearlyCalendarPage';
    // Client-side authorization gate. Viewing another user's calendar requires
    // team calendar access AND a resource/functional manager role. This mirrors
    // the redirect effect below and is used to block any data fetch until access
    // is confirmed. NOTE: this is a UX guard only - the server must independently
    // enforce authorization (tracked separately for the Availability/GRM APIs).
    const isCalendarAuthorized = useMemo(() => {
        // An unrecognised cross-user route is never authorized.
        if (isUnrecognisedRoute) {
            return false;
        }
        if (!isViewingOtherUser) {
            return true;
        }
        // Cross-user requires a known viewer: claims are resolved for the logged-in user, so without
        // an identity there is nothing to authorize against.
        if (!userAlias) {
            return false;
        }
        return CalendarAuthorizationService.getInstance().canViewOtherUserCalendar(targetRole);
    }, [isUnrecognisedRoute, isViewingOtherUser, targetRole, userAlias]);
    // Replicates the legacy GRM calendar.controller.js cross-user gate: viewing another
    // user's calendar requires team calendar access AND a resource/functional manager
    // role. Unauthorized attempts are routed to the platform 'unauthorized' state.
    // An unrecognised cross-user route lands here too rather than silently showing self.
    useEffect(() => {
        if (!isViewingOtherUser && !isUnrecognisedRoute) {
            return;
        }
        if (!isCalendarAuthorized) {
            const routeService = FxpServiceFactory.getServiceInstance(FXPServiceCatalog.FxpRouteService);
            routeService.navigatetoSpecificState('unauthorized');
        }
    }, [isViewingOtherUser, isUnrecognisedRoute, isCalendarAuthorized]);
    // Whether the viewer may edit the calendar currently on screen.
    //
    // Edit is strictly NARROWER than view: it requires the view gate to have passed first. Treating
    // them as independent let a viewer who was denied access - but who happened to hold Write_Team -
    // reach the mutation handlers in the window before the 'unauthorized' redirect effect ran.
    //
    // Self is always editable; cross-user is editable when the viewer holds Write_Team, matching prod
    // (grm.config.json grm-resourcedashboardfm). This replaces a blanket `!isViewingOtherUser`, which
    // was stricter than legacy and forced managers off this page to get an editable monthly view.
    const canEditTargetCalendar = useMemo(() => isCalendarAuthorized
        && (!isViewingOtherUser
            || CalendarAuthorizationService.getInstance().canEditOtherUserCalendar()), [isCalendarAuthorized, isViewingOtherUser]);
    // Business role drives two legacy rules: the CESCalendarEnabledForRoleId flighting parameter
    // and the role-159 force-to-yearly. Sourced from the same FXP call GRM uses
    // (grmuserprofile.service.js:18-20 -> UserInfoService.getCurrentUserData()).
    const businessRoleId = useMemo(() => { var _a, _b, _c; return (_c = (_b = (_a = getUserInfoService()) === null || _a === void 0 ? void 0 : _a.getCurrentUserData) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.businessRoleId; }, []);
    // Whether this user may see a monthly Resource Dashboard at all, and which view to open on.
    const viewGate = useCalendarViewGate({
        targetAlias,
        isViewingOtherUser,
        businessRoleId,
    });
    /*
     * Apply the resolved default view exactly once — and never over a choice the user already made.
     *
     * The Monthly control is now offered to every population, so it is clickable while the flags and
     * preference are still in flight. Without `userChoseModeRef` a click that lands before the gate
     * resolves is silently reverted a moment later by the saved default, which reads as "the Monthly
     * button does nothing".
     */
    const appliedDefaultViewRef = useRef(false);
    const userChoseModeRef = useRef(false);
    useEffect(() => {
        if (!viewGate.resolved || appliedDefaultViewRef.current || userChoseModeRef.current) {
            return;
        }
        appliedDefaultViewRef.current = true;
        setCurrentMode(viewGate.defaultMode);
    }, [viewGate.resolved, viewGate.defaultMode]);
    // Defence in depth: if the gate says monthly is not available, never sit in a monthly mode
    // (e.g. a stale mode after the flags resolve).
    useEffect(() => {
        if (viewGate.resolved && !viewGate.canUseMonthly && (currentMode === 'day' || currentMode === 'month')) {
            setCurrentMode('richcc-year');
        }
    }, [viewGate.resolved, viewGate.canUseMonthly, currentMode]);
    const userName = useMemo(() => {
        var _a;
        try {
            const userInfo = FxpServiceFactory.getServiceInstance(FXPServiceCatalog.UserInfoService);
            const userData = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.getCurrentUserData) === null || _a === void 0 ? void 0 : _a.call(userInfo);
            return (userData === null || userData === void 0 ? void 0 : userData.displayName) || (userData === null || userData === void 0 ? void 0 : userData.fullName) || '';
        }
        catch (_b) {
            return '';
        }
    }, []);
    const standardTimeZone = useMemo(() => {
        var _a, _b;
        try {
            const userInfo = FxpServiceFactory.getServiceInstance(FXPServiceCatalog.UserInfoService);
            return ((_b = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.getCurrentUserData) === null || _a === void 0 ? void 0 : _a.call(userInfo)) === null || _b === void 0 ? void 0 : _b.standardTimeZone) || 'UTC';
        }
        catch (_c) {
            return 'UTC';
        }
    }, []);
    /**
     * Timezone to stamp on a write.
     *
     * For the viewer's own calendar this is their profile zone. For someone else's it must be the
     * TARGET's zone -- writing a manager's zone onto a resource's event shifts it, which for a
     * distributed team can move an event across a day boundary. The target's zone arrives with the
     * calendar payload (`workPreferences.timezone`); until it does we fall back to the viewer's,
     * which is the pre-existing behaviour and only reachable before the first response lands.
     */
    const effectiveTimeZone = useMemo(() => (isViewingOtherUser ? (targetTimeZone || standardTimeZone) : standardTimeZone), [isViewingOtherUser, targetTimeZone, standardTimeZone]);
    const logTelemetry = useCallback((eventCode, properties) => {
        telemetryService.logEvent(TelemetryFeatures.YearlyCalendar, TelemetryActions.UserAction, eventCode, properties, telemetryService.getCorrelationId(), false);
    }, [telemetryService]);
    // addLog is a dev-time diagnostic helper; no-op in production.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const addLog = useCallback((_message) => { }, []);
    const handleDateChange = useCallback((date) => {
        setSelectedDate(date);
        setActiveDate(date);
        addLog(`Date changed to: ${date.toLocaleDateString()}`);
    }, [addLog]);
    const handleModeChange = useCallback((data) => {
        // Only the mode is applied here. The active date arrives through
        // onDateChange, which the calendar always fires alongside a date move;
        // data.activeDate is read from a closure and can lag by one render.
        setCurrentMode(data.mode);
        addLog(`Mode changed to: ${data.mode} at ${data.activeDate.toLocaleDateString()}`);
    }, [addLog]);
    // Monthly switch. Exactly ONE case navigates away instead of switching mode in place:
    // arrived from /cescalendar - return the user to the CES month grid, which is the monthly
    // counterpart of the calendar they came from. CES has no yearly view of its own
    // (ce-calendar.component.ts:199), which is why it hands off to this page for yearly.
    //
    // Every other entry -- self view OR cross-user, from the resource dashboard, left-nav, a deep
    // link or a direct URL -- switches mode client-side over the already-cached event window, with
    // no navigation and no GRM round trip.
    //
    // Cross-user used to navigate out to 'grm-resourcedashboardquicksearch' because this page was
    // read-only for other users and legacy monthly was not. That is no longer true (canEdit is now
    // permission-based, matching Write_Team in prod), and the navigation would now be actively
    // harmful: this app OWNS that state name, so the branch would route the page to itself in a loop.
    const switchToMonthly = useCallback(() => {
        // Record the intent immediately so a late-resolving default view cannot revert it.
        userChoseModeRef.current = true;
        // Hard stop: CES-enabled users have no monthly Resource Dashboard at all.
        if (!viewGate.canUseMonthly) {
            return;
        }
        if (monthlySource === 'cescalendar' && !isViewingOtherUser) {
            const routeService = FxpServiceFactory.getServiceInstance(FXPServiceCatalog.FxpRouteService);
            logTelemetry(EventCodes.Ev0083, {
                interaction: 'switch-to-monthly',
                isResource: isViewingOtherUser,
                target: 'cescalendar',
            });
            routeService.navigatetoSpecificState('cescalendar', {});
            return;
        }
        logTelemetry(EventCodes.Ev0083, {
            interaction: 'switch-to-monthly',
            isResource: isViewingOtherUser,
            target: 'react-monthly',
        });
        setCurrentMode('day');
        // Legacy persists the choice on every toggle (calendar.controller.js:1370-1371), but only for
        // the viewer's OWN calendar - mirroring switchToYearly. Toggling while looking at someone
        // else's calendar must not rewrite the viewer's saved default view.
        // Fire-and-forget: it must never block the switch.
        if (userAlias && !isViewingOtherUser) {
            yearlyCalendarBridge.saveUserPreference(userAlias, DEFAULT_CALENDAR_MODE_PREFERENCE, MONTHLY);
        }
    }, [
        viewGate.canUseMonthly,
        isViewingOtherUser,
        logTelemetry,
        monthlySource,
        userAlias,
        yearlyCalendarBridge,
    ]);
    const switchToYearly = useCallback(() => {
        userChoseModeRef.current = true;
        logTelemetry(EventCodes.Ev0083, {
            interaction: 'switch-to-yearly',
            isResource: isViewingOtherUser,
            target: 'react-yearly',
        });
        setCurrentMode('richcc-year');
        // Legacy: calendar.controller.js:1357-1358.
        if (userAlias && !isViewingOtherUser) {
            yearlyCalendarBridge.saveUserPreference(userAlias, DEFAULT_CALENDAR_MODE_PREFERENCE, YEARLY);
        }
    }, [isViewingOtherUser, logTelemetry, userAlias, yearlyCalendarBridge]);
    const handleDaySelect = useCallback((data) => {
        addLog(`Day selected: ${data.dt.date.toLocaleDateString()}`);
    }, [addLog]);
    const handleEventClick = useCallback((data) => {
        addLog(`Event clicked: "${data.event.name}" on ${data.dt.toLocaleDateString()}`);
        logTelemetry(EventCodes.Ev0083, {
            interaction: 'event-click',
            eventType: data.event.eventTypeName || 'unknown',
        });
        // Open event detail modal
        setDetailModalEvent(data.event);
    }, [addLog, logTelemetry]);
    const refreshEvents = useCallback((options) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!targetAlias) {
            setErrorMessage('Unable to resolve user alias for calendar data.');
            return;
        }
        // Do not fetch another user's calendar data until authorization is confirmed.
        // The redirect effect sends unauthorized users to the 'unauthorized' state;
        // this guard ensures no data request is issued in the meantime.
        if (!isCalendarAuthorized) {
            return;
        }
        const { startDate, endDate } = getCalendarRange(activeDate, currentMode);
        // Monthly (3 months) is always contained in the yearly window (12 months
        // from the same active month), so switching Yearly -> Monthly re-renders
        // from the events already in memory instead of round-tripping. Mutations
        // pass force:true because the cached window is no longer authoritative.
        const cached = fetchedWindowRef.current;
        if (!(options === null || options === void 0 ? void 0 : options.force) &&
            cached &&
            cached.alias === targetAlias &&
            cached.start <= startDate.getTime() &&
            cached.end >= endDate.getTime()) {
            // Invalidate any request still in flight. Without this, a slower fetch for
            // a window the user has already navigated away from would still be
            // "latest" and would overwrite the events this cached view is rendering.
            refreshRequestIdRef.current++;
            setLoading(false);
            return;
        }
        const requestId = ++refreshRequestIdRef.current;
        setLoading(true);
        setErrorMessage(null);
        const startTime = Date.now();
        try {
            const response = yield yearlyCalendarBridge.fetchCalendarData({
                alias: targetAlias,
                startDate,
                endDate,
                fetchProjectEventsByRequestedDates: true,
                // The API sources the Outlook mailbox from the caller, not from `alias`, so requesting it
                // on someone else's calendar returns OUR events, working hours and timezone. Omitting it
                // also lets the API fall back to the target's own OneProfile working days.
                includeOutlookEvents: !isViewingOtherUser,
            });
            // A newer refresh started while this one was in flight - discard the stale
            // result so a slow response for a previous year cannot overwrite the
            // currently displayed data.
            if (requestId !== refreshRequestIdRef.current) {
                return;
            }
            const ceEvents = (response === null || response === void 0 ? void 0 : response.eventDetails) || [];
            const outlookDetails = response === null || response === void 0 ? void 0 : response.outlookCalendarDetails;
            const outlookEvents = (outlookDetails === null || outlookDetails === void 0 ? void 0 : outlookDetails.eventDetails) || [];
            const mappedEvents = [
                ...ceEvents.map(mapCEEventToCalendarEvent),
                ...outlookEvents.map(mapOutlookEventToCalendarEvent),
            ];
            // Deduplicate public holidays: the API may return the same holiday as both
            // a 'publicholiday'/'msvacation' event AND a 'nonproject' event with
            // AssignmentType 'Public Holiday'. Keep only one per date.
            const holidayDateSet = new Set();
            const deduplicatedEvents = mappedEvents.filter(evt => {
                if (!evt.isPublicHoliday)
                    return true;
                const dateKey = evt.startDt instanceof Date
                    ? `${evt.startDt.getFullYear()}-${evt.startDt.getMonth()}-${evt.startDt.getDate()}`
                    : String(evt.startDt);
                if (holidayDateSet.has(dateKey))
                    return false;
                holidayDateSet.add(dateKey);
                return true;
            });
            setEvents(deduplicatedEvents);
            setNonWorkDays(getNonWorkDays((_a = outlookDetails === null || outlookDetails === void 0 ? void 0 : outlookDetails.workPreferences) === null || _a === void 0 ? void 0 : _a.workingDays));
            // The target's own timezone, when the payload carries it. Required so that an event added to
            // ANOTHER user's calendar is stored in their zone rather than the viewer's.
            setTargetTimeZone(((_b = outlookDetails === null || outlookDetails === void 0 ? void 0 : outlookDetails.workPreferences) === null || _b === void 0 ? void 0 : _b.timezone) || '');
            fetchedWindowRef.current = {
                alias: targetAlias,
                start: startDate.getTime(),
                end: endDate.getTime(),
            };
            const durationMs = Date.now() - startTime;
            logTelemetry(EventCodes.Ev0083, {
                records: mappedEvents.length,
                mode: currentMode,
            });
            logTelemetry(EventCodes.Ev0089, {
                durationMs,
                mode: currentMode,
            });
        }
        catch (error) {
            if (requestId !== refreshRequestIdRef.current) {
                return;
            }
            setEvents([]);
            setErrorMessage('Failed to load calendar events.');
            fetchedWindowRef.current = null;
            logTelemetry(EventCodes.Ev0084, {
                mode: currentMode,
            });
            telemetryService.logError(telemetrySource, 'refreshEvents', JSON.stringify(error), 500);
        }
        finally {
            // Only the latest request controls the shared loading flag.
            if (requestId === refreshRequestIdRef.current) {
                setLoading(false);
            }
        }
    }), [
        targetAlias,
        activeDate,
        currentMode,
        isCalendarAuthorized,
        isViewingOtherUser,
        yearlyCalendarBridge,
        logTelemetry,
        telemetryService,
    ]);
    const upsertNonProjectOperation = useCallback((operation, actionCode, successLabel) => __awaiter(void 0, void 0, void 0, function* () {
        if (upsertInFlightRef.current) {
            return;
        }
        if (!canEditTargetCalendar) {
            showToast('You do not have permission to modify this calendar.', 'error');
            return;
        }
        if (!targetAlias) {
            addLog('Skipping update because the target alias is unavailable.');
            return;
        }
        upsertInFlightRef.current = true;
        try {
            // `targetAlias`, not the viewer. On a cross-user calendar the event must be written to the
            // resource being viewed; passing the viewer's alias here silently filed the manager's edits
            // onto their own calendar.
            const payload = yearlyCalendarBridge.buildUpsertPayloadFromOperation(operation, targetAlias, effectiveTimeZone);
            const response = yield yearlyCalendarBridge.upsertNonProjectSchedules(payload);
            // API returns 'C600' for success, 'GRM601' for business error (matching e360 NonProjectPanel)
            const isSuccess = response.length > 0 && response.some(item => (item === null || item === void 0 ? void 0 : item.responseCode) === 'C600');
            const hasError = response.length > 0 && response.some(item => (item === null || item === void 0 ? void 0 : item.responseCode) === 'GRM601');
            if (hasError) {
                let errorMsg = `Non-project ${successLabel} failed.`;
                try {
                    const errItem = response.find(item => (item === null || item === void 0 ? void 0 : item.responseCode) === 'GRM601');
                    if (errItem === null || errItem === void 0 ? void 0 : errItem.statusMessage) {
                        const jsonStart = errItem.statusMessage.indexOf('{');
                        if (jsonStart >= 0) {
                            const parsed = JSON.parse(errItem.statusMessage.substring(jsonStart));
                            if (parsed === null || parsed === void 0 ? void 0 : parsed.ExceptionIdentifier) {
                                errorMsg = `${parsed.ErrorMessage || errorMsg} (Ref: ${parsed.ExceptionIdentifier})`;
                            }
                            else {
                                errorMsg = parsed.ErrorMessage || errorMsg;
                            }
                        }
                    }
                }
                catch ( /* use default error message */_c) { /* use default error message */ }
                showToast(errorMsg, 'error');
                logTelemetry(EventCodes.Ev0088, { action: operation.action });
                return;
            }
            if (isSuccess) {
                showToast('Event details updated successfully', 'success');
            }
            else if (response.length === 0) {
                // Empty response = API call likely failed (caught in bridge service)
                showToast(`Failed to ${successLabel} event. Please try again.`, 'error');
                return;
            }
            logTelemetry(actionCode, { action: operation.action });
            yield refreshEvents({ force: true });
        }
        finally {
            upsertInFlightRef.current = false;
        }
    }), [
        targetAlias,
        yearlyCalendarBridge,
        effectiveTimeZone,
        addLog,
        logTelemetry,
        refreshEvents,
        showToast,
        canEditTargetCalendar,
    ]);
    const handlePopupLeftAction = useCallback((data) => {
        if (!canEditTargetCalendar)
            return;
        addLog(`Popup add action on ${data.data.dt.toLocaleDateString()}`);
        // Open add modal instead of directly calling API
        setAddEditModal({ open: true, mode: 'add', targetDate: data.data.dt, event: null });
    }, [addLog, canEditTargetCalendar]);
    const handleDeleteEventClick = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _d;
        if (!canEditTargetCalendar) {
            showToast('You do not have permission to modify this calendar.', 'error');
            return;
        }
        const category = (data.event.eventTypeName || '').toLowerCase();
        // Block deletion of holidays (whether they arrive as publicholiday, msvacation, or nonproject with holiday AssignmentType)
        if (data.event.isPublicHoliday) {
            showToast(`Cannot delete ${data.event.name} — public holidays are system-managed.`, 'error');
            return;
        }
        if (category !== 'nonproject') {
            showToast(`Cannot delete ${data.event.name} — only non-project events are editable.`, 'error');
            return;
        }
        const id = ((_d = data.event.customProperties) === null || _d === void 0 ? void 0 : _d.Id) || data.event.eventId;
        if (!id) {
            showToast(`Cannot delete ${data.event.name} — event identifier is missing.`, 'error');
            return;
        }
        // Show the delete confirmation modal instead of deleting directly
        setDeleteConfirmEvent(data.event);
    }), [showToast, canEditTargetCalendar]);
    /** Called when user confirms deletion from the standalone DeleteConfirmationModal */
    const handleDeleteConfirmFromPopup = useCallback((event, shouldDeleteSeries) => __awaiter(void 0, void 0, void 0, function* () {
        var _e, _f, _g;
        setDeleteConfirmEvent(null);
        if (!canEditTargetCalendar) {
            showToast('You do not have permission to modify this calendar.', 'error');
            return;
        }
        const response = yield yearlyCalendarBridge.deleteNonProjectSchedule(event, shouldDeleteSeries);
        const isSuccess = response.length > 0 && response.some(item => (item === null || item === void 0 ? void 0 : item.responseCode) === 'C600');
        const hasError = response.length > 0 && response.some(item => (item === null || item === void 0 ? void 0 : item.responseCode) === 'GRM601');
        if (hasError || (!isSuccess && response.length === 0)) {
            showToast(`Failed to delete ${event.name}. Please try again.`, 'error');
            logTelemetry(EventCodes.Ev0088, { action: 'delete' });
            return;
        }
        const isRecurrent = !!(((_e = event.customProperties) === null || _e === void 0 ? void 0 : _e.IsRecurrent) || ((_f = event.customProperties) === null || _f === void 0 ? void 0 : _f.isRecurrent)
            || (((_g = event.customProperties) === null || _g === void 0 ? void 0 : _g.RecurrencePattern) && event.customProperties.RecurrencePattern !== 'None'));
        const successMsg = shouldDeleteSeries && isRecurrent
            ? `Deleted all future recurrences of ${event.name} successfully.`
            : `Deleted ${event.name} successfully.`;
        showToast(successMsg, 'success');
        logTelemetry(EventCodes.Ev0087, { action: 'delete' });
        yield refreshEvents({ force: true });
    }), [yearlyCalendarBridge, logTelemetry, refreshEvents, showToast, canEditTargetCalendar]);
    const handleAddOrEditEventClick = useCallback((data) => {
        if (!canEditTargetCalendar) {
            showToast('You do not have permission to modify this calendar.', 'error');
            return;
        }
        addLog(`Add/edit event requested on ${data.forDate.toLocaleDateString()}`);
        setAddEditModal({
            open: true,
            mode: data.event ? 'edit' : 'add',
            targetDate: data.forDate,
            event: data.event || null,
        });
    }, [addLog, canEditTargetCalendar, showToast]);
    /** When DELETE EVENT is clicked in the detail modal, close it and open the delete confirmation modal */
    const handleDeleteFromDetailModal = useCallback((event) => {
        var _a;
        if (!isEditableFutureNonProjectEvent(event)) {
            showToast(`Cannot delete ${event.name} — only future non-project events are deletable.`, 'error');
            return;
        }
        const id = ((_a = event.customProperties) === null || _a === void 0 ? void 0 : _a.Id) || event.eventId;
        if (!id) {
            showToast(`Cannot delete ${event.name} — event identifier is missing.`, 'error');
            return;
        }
        // Close detail modal, open delete confirmation in its place
        setDetailModalEvent(null);
        setDeleteConfirmEvent(event);
    }, [showToast]);
    const handleEditFromDetailModal = useCallback((event) => {
        if (!isEditableFutureNonProjectEvent(event)) {
            showToast(`Cannot edit ${event.name} — only future non-project events are editable.`, 'error');
            return;
        }
        setDetailModalEvent(null);
        setAddEditModal({
            open: true,
            mode: 'edit',
            targetDate: event.startDt instanceof Date ? event.startDt : new Date(event.startDt),
            event,
        });
    }, [showToast]);
    // --- Modal action handlers ---
    const handleAddEditSave = useCallback((formData) => __awaiter(void 0, void 0, void 0, function* () {
        setAddEditModal(prev => (Object.assign(Object.assign({}, prev), { open: false })));
        const isEdit = formData.sourceEvent != null;
        const operation = {
            action: isEdit ? 'update' : 'create',
            date: formData.startDateTime,
            event: isEdit
                ? Object.assign(Object.assign({}, formData.sourceEvent), { startDt: formData.startDateTime, endDt: formData.endDateTime, customProperties: Object.assign(Object.assign({}, (formData.sourceEvent.customProperties || {})), { AssignmentType: formData.eventType, comments: formData.comments, IsRecurrent: formData.isRecurrent, RecurrencePattern: formData.isRecurrent ? formData.recurrencePattern : null, RecurrenceEndsAfter: formData.isRecurrent ? formData.recurrenceEndsAfter : null, RecurrenceEndsBy: formData.isRecurrent && formData.recurrenceEndsBy && !isNaN(formData.recurrenceEndsBy.getTime()) ? formData.recurrenceEndsBy.toISOString() : null, IsRecurrenceEndDate: formData.isRecurrent ? formData.isRecurrenceEndDate : false, ShowAs: formData.showAs || 'Busy', IsBusy: formData.showAs === 'Free' ? false : true }) }) : {
                id: 0,
                name: formData.eventType,
                startDt: formData.startDateTime,
                endDt: formData.endDateTime,
                eventTypeName: 'NonProject',
                customProperties: {
                    AssignmentType: formData.eventType,
                    comments: formData.comments,
                    IsRecurrent: formData.isRecurrent,
                    RecurrencePattern: formData.isRecurrent ? formData.recurrencePattern : null,
                    RecurrenceEndsAfter: formData.isRecurrent ? formData.recurrenceEndsAfter : null,
                    RecurrenceEndsBy: formData.isRecurrent && formData.recurrenceEndsBy && !isNaN(formData.recurrenceEndsBy.getTime()) ? formData.recurrenceEndsBy.toISOString() : null,
                    IsRecurrenceEndDate: formData.isRecurrent ? formData.isRecurrenceEndDate : false,
                    ShowAs: formData.showAs || 'Busy',
                    IsBusy: formData.showAs === 'Free' ? false : true,
                },
            },
        };
        yield upsertNonProjectOperation(operation, isEdit ? EventCodes.Ev0086 : EventCodes.Ev0085, isEdit ? 'update' : 'create');
    }), [upsertNonProjectOperation]);
    // 'day' is the month grid and 'month' is the month picker reached from its
    // title - both are monthly states. 'year'/'richcc-year' are the yearly ones.
    const isMonthlyView = currentMode === 'day' || currentMode === 'month';
    const config = useMemo(() => (Object.assign(Object.assign(Object.assign({}, DEFAULT_CALENDAR_CONFIG), { nonWorkDays, datepickerMode: 'richcc-year', navigation: true }), (isMonthlyView
        ? {
            preventModeToggle: true,
            expandedMode: true,
            showMarkerForMoreEvents: true,
            showWeeks: false,
        }
        : {}))), [nonWorkDays, isMonthlyView]);
    /**
     * The two side mini-months: the month after the main one and the one after
     * that (calendar.controller.js:500-501). Derived from the main active date at
     * render time so they always track navigation - never from literals, which is
     * how legacy's `self.today()` ended up pinned to 2016.
     */
    const miniMonthDates = useMemo(() => ([
        new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1),
        new Date(activeDate.getFullYear(), activeDate.getMonth() + 2, 1),
    ]), [activeDate]);
    // Promoting a side month moves the main grid to it. The window shifts, so
    // this does refetch - the two new side months are not in the cached range.
    const handleMiniMonthSelect = useCallback((month, year) => {
        const promoted = new Date(year, month, 1);
        setActiveDate(promoted);
        setSelectedDate(promoted);
        logTelemetry(EventCodes.Ev0083, { interaction: 'mini-month-select' });
    }, [logTelemetry]);
    const sideMiniMonths = useMemo(() => {
        // Only the month grid gets side calendars; the month picker does not.
        if (currentMode !== 'day') {
            return null;
        }
        return miniMonthDates.map((monthDate, index) => (React.createElement(MiniMonth, { key: `mini-${monthDate.getFullYear()}-${monthDate.getMonth()}`, activeDate: monthDate, events: events, config: config, uidPrefix: `mini-month-${index + 1}`, onMonthSelect: handleMiniMonthSelect })));
    }, [currentMode, miniMonthDates, events, config, handleMiniMonthSelect]);
    const popupSettings = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_POPUP_SETTINGS), { hidden: false, showLeft: canEditTargetCalendar, leftLabel: 'Add Event' })), [canEditTargetCalendar]);
    useEffect(() => {
        logTelemetry(EventCodes.Ev0082, {
            mode: currentMode,
        });
        // Fetch non-project event types from GRM domain data API on mount
        yearlyCalendarBridge.fetchNonProjectEventTypes().then(eventTypes => {
            if (eventTypes.length > 0) {
                setNpEventTypeNames(eventTypes
                    .filter(et => et.EventTypeName.toLowerCase() !== 'public holiday')
                    .map(et => et.EventTypeName));
            }
        });
        // Intentionally once per page load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        refreshEvents();
    }, [currentMode, refreshEvents]);
    return (React.createElement("div", { style: pageStyles.container },
        React.createElement("div", { style: pageStyles.userBanner }, isViewingOtherUser
            ? `${targetAlias.toUpperCase()}'S CALENDAR`
            : userName ? `${userName.toUpperCase()}'S HOME` : userAlias ? `${userAlias.toUpperCase()}'S HOME` : 'MY HOME'),
        isViewingOtherUser && targetManagerAlias ? (React.createElement(ResourceNamePicker, { managerAlias: targetManagerAlias, selectedAlias: targetAlias, onSelectResource: handleSelectResource })) : null,
        React.createElement("div", { style: pageStyles.calendarWrapper },
            React.createElement("div", { style: Object.assign(Object.assign({}, pageStyles.header), { display: 'flex', alignItems: 'center' }) },
                React.createElement("h1", { style: Object.assign(Object.assign({}, pageStyles.title), { textTransform: 'uppercase' }) }, "Calendar"),
                errorMessage && (React.createElement("p", { role: "alert", style: Object.assign(Object.assign({}, pageStyles.subtitle), { color: '#a80000' }) }, errorMessage)),
                React.createElement("div", { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' } },
                    React.createElement(CalendarViewSwitcher, { activeView: isMonthlyView ? 'monthly' : 'yearly', showMonthly: viewGate.canUseMonthly, onMonthlyClick: switchToMonthly, onYearlyClick: switchToYearly }))),
            toast && (React.createElement("div", { style: Object.assign(Object.assign({}, pageStyles.toast), (toast.type === 'success' ? pageStyles.toastSuccess : pageStyles.toastError)), role: "alert" },
                React.createElement("span", null, toast.message),
                React.createElement("button", { style: pageStyles.toastDismiss, onClick: () => setToast(null), "aria-label": "Dismiss" }, "\u00D7"))),
            React.createElement(YearlyCalendar, { selectedDate: selectedDate, events: events, config: config, popupSettings: popupSettings, viewMode: currentMode, activeDate: activeDate, sideContent: sideMiniMonths, onDateChange: handleDateChange, onModeChange: handleModeChange, onDaySelect: handleDaySelect, onEventClick: handleEventClick, onPopupLeftAction: handlePopupLeftAction, onAddOrEditEventClick: handleAddOrEditEventClick, onDeleteEventClick: handleDeleteEventClick, calendarName: "Resource Calendar" })),
        loading && (React.createElement("div", { role: "status", "aria-live": "polite", style: {
                position: 'fixed', bottom: '16px', right: '16px',
                fontSize: '13px', color: '#605e5c', opacity: 0.6,
                zIndex: 100,
            } }, "Loading calendar events...")),
        React.createElement(EventDetailModal, { isOpen: detailModalEvent != null, event: detailModalEvent, userAlias: targetAlias, canEdit: canEditTargetCalendar && isEditableFutureNonProjectEvent(detailModalEvent), onEdit: handleEditFromDetailModal, onDelete: handleDeleteFromDetailModal, onClose: () => setDetailModalEvent(null) }),
        React.createElement(AddEditEventModal, { isOpen: addEditModal.open, mode: addEditModal.mode, targetDate: addEditModal.targetDate, existingEvent: addEditModal.event, eventTypeOptions: npEventTypeNames.length > 0 ? npEventTypeNames : undefined, onClose: () => setAddEditModal(prev => (Object.assign(Object.assign({}, prev), { open: false }))), onSave: handleAddEditSave }),
        React.createElement(DeleteConfirmationModal, { isOpen: deleteConfirmEvent != null, event: deleteConfirmEvent, onConfirm: handleDeleteConfirmFromPopup, onCancel: () => setDeleteConfirmEvent(null) })));
};
export default YearlyCalendarPage;
//# sourceMappingURL=YearlyCalendarPage.js.map